import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
  useMediaQuery,
  IconButton,
  Tooltip,
  Snackbar,
  Badge,
  Popover,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  FolderOpen as FolderOpenIcon,
  Route as RouteIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  DirectionsRun as DirectionsRunIcon,
  InfoOutlined as InfoOutlinedIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { downloadSharedRunningWorkout, detectRaceForFit } from '../../api/runningApi';
import WorkoutPickerDrawer from './WorkoutPickerDrawer';
import GarminCalendar from '../garmin/GarminCalendar';
import { importGarminActivity } from '../../api/garminApi';
import RaceDetailDialog from './RaceDetailDialog';
import ClubPickerDrawer from './ClubPickerDrawer';
import { getMyPendingCount, getMyClubFiles, analyzeClubFile } from '../../api/clubApi';

const fmtTime = (secs) => {
  if (!secs) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

const RunningFileUpload = ({ onFileUpload, onPredictionSelect = () => {}, loading,
                             onMultiFilePredict = null,
                             // Bumped by a parent that wants the My Files drawer opened —
                             // the dashboard sends people here to mark runs for multi-file
                             // prediction, and the drawer's own state lives in here.
                             openFilesSignal = 0,
                             // Passed straight through: tells the drawer the user arrived
                             // to mark runs, so it points at the column that does it.
                             markRunsSignal = 0,
                             // Called when the My Files drawer closes, so a parent can
                             // re-read anything the user changed in there.
                             onFilesDrawerClose = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // File state
  const [file, setFile] = useState(null);
  const [reusedFileId, setReusedFileId] = useState(null);
  const [reusedFilename, setReusedFilename] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [description, setDescription] = useState('');


  // Race linking
  const [selectedRace, setSelectedRace] = useState(null);
  const [raceDetailOpen, setRaceDetailOpen] = useState(false);
  const [snapToGpx, setSnapToGpx] = useState(false);
  const [applyGpsCorrections, setApplyGpsCorrections] = useState(false);
  const [snapInfoAnchor, setSnapInfoAnchor] = useState(null);
  const [gpsInfoAnchor, setGpsInfoAnchor] = useState(null);

  /**
   * Busy state for loading a club file.
   *
   * Every other path out of this form hands the work to the parent, which owns the
   * `loading` prop the button reads. Analysing a club file is the exception — it
   * calls the API from here and navigates itself — so it drove no indicator at all
   * and the button sat there looking idle through a full FIT parse.
   */
  const [clubFileLoading, setClubFileLoading] = useState(false);
  const busy = loading || clubFileLoading;

  /**
   * A race we think this file belongs to, worked out from its start position and
   * length the moment it is picked.
   *
   * Users routinely analyse a race file without linking the race and lose the
   * checkpoints, cutoffs, plan and prediction comparison that depend on one. The
   * "Link Race" button already pulsed at them and it did not help — a prompt that
   * NAMES the race is a different proposition. It stays a suggestion: linking the
   * wrong race quietly corrupts every checkpoint-derived number for that file.
   */
  const [raceSuggestions, setRaceSuggestions] = useState([]);
  // Asked once per file, on the way to Analyze. A notice beside the button is
  // still skippable — and skipping it is exactly the behaviour being fixed — so
  // the question is put in the path of the click instead. It is a question, not
  // an auto-link: a wrong race silently corrupts every checkpoint-derived figure
  // for that file, so the user's answer is what decides it.
  const [raceConfirmOpen, setRaceConfirmOpen] = useState(false);
  const [raceAsked, setRaceAsked] = useState(false);

  // Long wait snackbar
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);
  const loadingTimerRef = useRef(null);

  useEffect(() => {
    if (busy) {
      loadingTimerRef.current = setTimeout(() => setShowLongWaitMessage(true), 5000);
    } else {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLongWaitMessage(false);
    }
    return () => { if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current); };
  }, [busy]);

  // Drawers
  const [filePickerOpen, setFilePickerOpen] = useState(false);

  // Open My Files when the parent asks. Guarded on > 0 so the drawer does not
  // appear on first render, only when the signal is actually bumped.
  useEffect(() => {
    if (openFilesSignal > 0) setFilePickerOpen(true);
  }, [openFilesSignal]);
  const [racePickerOpen, setRacePickerOpen] = useState(false);
  const [clubPickerOpen, setClubPickerOpen] = useState(false);
  const [clubPendingCount, setClubPendingCount] = useState(0);
  const [allClubFiles, setAllClubFiles] = useState([]);
  const [allClubFilesLoading, setAllClubFilesLoading] = useState(false);
  const [allClubFilesLoaded, setAllClubFilesLoaded] = useState(false);

  // Load pending club requests count for notification badge
  useEffect(() => {
    if (!isAuthenticated) return;
    getMyPendingCount()
      .then((data) => setClubPendingCount(data.pending_count || 0))
      .catch(() => {});
  }, [isAuthenticated]);

  // Selected prediction (from My Predictions tab in file picker)
  const [selectedPrediction, setSelectedPrediction] = useState(null);

  // Selected club file (from Club Files tab in file picker) — deferred until Analyze pressed
  const [selectedClubFile, setSelectedClubFile] = useState(null);

  const hasFile = !!(file || reusedFileId);
  const hasSelection = hasFile || !!selectedPrediction || !!selectedClubFile;


  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    validateAndSetFile(e.dataTransfer.files[0]);
  }, []);

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.fit')) {
      setError(t('pleaseSelectFitFile', 'Please select a FIT file'));
      return;
    }
    setFile(selectedFile);
    setReusedFileId(null);
    setReusedFilename(null);
    setSelectedPrediction(null);
    setSelectedClubFile(null);
    setError('');

    // Fire and forget — a failed suggestion must never block an upload.
    setRaceSuggestions([]);
    setRaceAsked(false);
    detectRaceForFit(selectedFile)
      .then((res) => {
        const found = res?.candidates || [];
        setRaceSuggestions(found);
        // Ask straight away, while the file is still what the user is thinking
        // about. Deferring it to the Analyze click meant the button did
        // something other than what it said, which is its own kind of wrong.
        if (found.length > 0) {
          setRaceAsked(true);
          setRaceConfirmOpen(true);
        }
      })
      .catch(() => setRaceSuggestions([]));
  };

  // Lazy-load all club files when the file picker opens for the first time
  const handleFilePickerOpen = useCallback(() => {
    setFilePickerOpen(true);
    if (!isAuthenticated || allClubFilesLoaded || allClubFilesLoading) return;
    setAllClubFilesLoading(true);
    getMyClubFiles()
      .then((data) => { setAllClubFiles(data); setAllClubFilesLoaded(true); })
      .catch(() => {})
      .finally(() => setAllClubFilesLoading(false));
  }, [isAuthenticated, allClubFilesLoaded, allClubFilesLoading]);

  // File picker: selects workout from My Files, Community, My Predictions, or Club Files
  const handleFilePickerSelect = useCallback(async (source) => {
    setFilePickerOpen(false);

    if (source.type === 'clubFile') {
      setFile(null);
      setReusedFileId(null);
      setReusedFilename(null);
      setSelectedPrediction(null);
      setSelectedClubFile(source);
      setError('');
      return;
    }

    if (source.type === 'prediction') {
      const record = source.record || {};
      setFile(null);
      setReusedFileId(null);
      setReusedFilename(null);
      setSelectedClubFile(null);
      setSelectedPrediction(record);
      setError('');
      return;
    }

    setSelectedPrediction(null);
    setSelectedClubFile(null);

    if (source.type === 'garmin') {
      // The activity is staged on the server, so importing it just promotes it
      // into the user's files — from there it is an ordinary saved FIT and the
      // reuse path below handles everything else.
      try {
        setError('');
        const result = await importGarminActivity(source.activityId);
        setFile(null);
        setReusedFileId(result.file_id);
        setReusedFilename(result.filename || `garmin_${source.activityId}.fit`);
        setSelectedRace(null);
        setDescription('');
      } catch (err) {
        setError(
          err?.response?.data?.detail
          || t('garminImportFailed', 'Could not import that Garmin activity')
        );
      }
      return;
    }

    if (source.type === 'myfile') {
      const record = source.record || {};
      setFile(null);
      setReusedFileId(source.fileId);
      setReusedFilename(record.filename || source.fileId);
      setError('');

      // Restore GPS settings and description from saved record
      if (record.apply_gps_corrections != null) setApplyGpsCorrections(record.apply_gps_corrections);
      if (record.snap_to_gpx != null) setSnapToGpx(record.snap_to_gpx);
      setDescription(record.description || '');

      // Restore race pairing from record
      if (record.race_id && record.race_name) {
        setSelectedRace({
          id: record.race_id,
          name: record.race_name,
        });
      } else {
        setSelectedRace(null);
      }
    } else if (source.type === 'community') {
      const record = source.record || {};
      try {
        const blob = await downloadSharedRunningWorkout(source.sharedId);
        const filename = record.filename || `community_${source.sharedId}.fit`;
        const fitFile = new File([blob], filename, { type: 'application/octet-stream' });
        setFile(fitFile);
        setReusedFileId(null);
        setReusedFilename(null);
        setError('');
        setSnapToGpx(false);
        if (record.race_id && record.race_name) {
          setSelectedRace({ id: record.race_id, name: record.race_name });
        } else {
          setSelectedRace(null);
        }
      } catch {
        setError(t('failedToDownloadCommunity', 'Failed to download community workout'));
      }
    }
  }, [t]);

  // Race picker: selects a race
  const handleRacePickerSelect = useCallback((source) => {
    setRacePickerOpen(false);
    if (source.type === 'race') {
      setSelectedRace(source.record || null);
      if (!source.record?.has_gpx) setSnapToGpx(false);
    }
  }, []);

  const handleSubmit = async () => {
    // Safety net only. The question is normally asked the moment the file is
    // picked; this catches the case where detection was still running then — a
    // file without a usable session message takes a second or so — and the user
    // reached the button first. Never fires twice: raceAsked covers both paths.
    if (!selectedRace && !raceAsked && raceSuggestions.length > 0 && (file || reusedFileId)) {
      setRaceAsked(true);
      setRaceConfirmOpen(true);
      return;
    }
    if (selectedPrediction) {
      onPredictionSelect(selectedPrediction.prediction_session_id || selectedPrediction.id);
      return;
    }
    if (selectedClubFile) {
      setError('');
      setClubFileLoading(true);
      try {
        const workoutData = await analyzeClubFile(selectedClubFile.clubId, selectedClubFile.clubFileId);
        navigate('/running/analysis', {
          state: {
            workoutData,
            clubContext: {
              clubId: selectedClubFile.clubId,
              clubName: selectedClubFile.clubName,
              clubFileId: selectedClubFile.clubFileId,
              contributorEmail: selectedClubFile.contributorEmail,
              filename: selectedClubFile.filename,
            },
          },
        });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(Array.isArray(detail) ? detail.map(e => e.msg).join('; ') : detail || t('failedToLoadClubFile', 'Failed to load club file'));
      } finally {
        setClubFileLoading(false);
      }
      return;
    }
    if (!file && !reusedFileId) {
      setError(t('pleaseSelectFitFile', 'Please select a FIT file'));
      return;
    }
    try {
      const params = {
        race_id: selectedRace?.id || null,
        snap_to_gpx: snapToGpx,
        apply_gps_corrections: applyGpsCorrections,
        reused_file_id: reusedFileId || null,
        description: description || null,
      };
      await onFileUpload(reusedFileId ? null : file, params);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Upload failed. Please try again.');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={3}
        sx={{ p: { xs: 2, md: 3 }, backgroundColor: colors.grey[800], borderRadius: '12px' }}
      >
        {/* ── Drop zone + My Files button ─────────────────────────────── */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${dragActive ? colors.blueAccent[400] : hasFile ? colors.greenAccent[700] : (selectedPrediction || selectedClubFile) ? colors.blueAccent[700] : colors.grey[700]}`,
            borderRadius: '10px',
            p: { xs: 2, md: 2.5 },
            textAlign: 'center',
            backgroundColor: dragActive
              ? colors.blueAccent[900]
              : hasFile
              ? `${colors.greenAccent[900]}55`
              : (selectedPrediction || selectedClubFile)
              ? `${colors.blueAccent[900]}55`
              : colors.grey[900],
            cursor: 'pointer',
            transition: 'all 0.25s ease',
          }}
          onClick={() => document.getElementById('running-file-input').click()}
        >
          <input
            type="file"
            id="running-file-input"
            accept=".fit"
            style={{ display: 'none' }}
            onChange={(e) => validateAndSetFile(e.target.files[0])}
          />

          {selectedPrediction ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CheckCircleIcon sx={{ color: colors.blueAccent[400], fontSize: 28 }} />
              <Box textAlign="left">
                <Typography variant="body1" color={colors.blueAccent[300]} fontWeight={600} noWrap>
                  {selectedPrediction.race_name || 'Prediction'}
                </Typography>
                <Typography variant="caption" color={colors.grey[400]}>
                  {[
                    selectedPrediction.fit_filename,
                    selectedPrediction.race_distance_km ? `${selectedPrediction.race_distance_km.toFixed(1)} km` : null,
                    selectedPrediction.predicted_total_time_s ? fmtTime(selectedPrediction.predicted_total_time_s) : null,
                  ].filter(Boolean).join(' · ')} · {t('clickToChange', 'click to change')}
                </Typography>
              </Box>
            </Box>
          ) : selectedClubFile ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CheckCircleIcon sx={{ color: colors.blueAccent[400], fontSize: 28 }} />
              <Box textAlign="left">
                <Typography variant="body1" color={colors.blueAccent[300]} fontWeight={600} noWrap>
                  {selectedClubFile.filename}
                </Typography>
                <Typography variant="caption" color={colors.grey[400]}>
                  {[selectedClubFile.clubName, selectedClubFile.contributorEmail].filter(Boolean).join(' · ')} · {t('clickToChange', 'click to change')}
                </Typography>
              </Box>
            </Box>
          ) : hasFile ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CheckCircleIcon sx={{ color: colors.greenAccent[400], fontSize: 28 }} />
              <Box textAlign="left">
                <Typography variant="body1" color={colors.greenAccent[300]} fontWeight={600} noWrap>
                  {file ? file.name : reusedFilename}
                </Typography>
                <Typography variant="caption" color={colors.grey[400]}>
                  {file ? `${(file.size / 1024).toFixed(0)} KB · ${t('clickToChange', 'click to change')}` : t('fromMyFilesChange', 'from My Files · click to change')}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 36, color: colors.blueAccent[400], mb: 1 }} />
              <Typography variant="body1" color={colors.grey[200]} mb={0.25}>
                {t('dropRunningFitFile', 'Drop your running FIT file here or click to browse')}
              </Typography>
              <Typography variant="caption" color={colors.grey[500]}>
                {t('acceptedFormats', 'Accepted formats: .fit')}
              </Typography>
            </>
          )}
        </Box>

        {/* My Files + My Clubs quick-actions */}
        <Box display="flex" alignItems="center" gap={1.5} mt={1.5} mb={hasFile ? 2.5 : 0} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={<FolderOpenIcon />}
            onClick={(e) => {
              e.stopPropagation();
              if (!isAuthenticated) { setError(t('pleaseLoginViewFiles', 'Please login to view your saved files')); return; }
              handleFilePickerOpen();
            }}
            disabled={!isAuthenticated}
            sx={{
              color: colors.blueAccent[400],
              borderColor: colors.blueAccent[700],
              textTransform: 'none',
              '&:hover': { borderColor: colors.blueAccent[400], backgroundColor: `${colors.blueAccent[900]}80` },
            }}
          >
            {t('myRunningFiles', 'My Running Files')}
          </Button>

          <Badge
            badgeContent={clubPendingCount}
            color="error"
            invisible={!isAuthenticated || clubPendingCount === 0}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<GroupIcon />}
              onClick={(e) => {
                e.stopPropagation();
                if (!isAuthenticated) { setError(t('pleaseLoginViewClubs', 'Please login to view your clubs')); return; }
                setClubPickerOpen(true);
              }}
              disabled={!isAuthenticated}
              sx={{
                color: colors.blueAccent[400],
                borderColor: colors.blueAccent[700],
                textTransform: 'none',
                '&:hover': { borderColor: colors.blueAccent[400], backgroundColor: `${colors.blueAccent[900]}80` },
              }}
            >
              {t('myClubs', 'My Clubs')}
            </Button>
          </Badge>

          {!hasSelection && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DirectionsRunIcon />}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const response = await fetch(process.env.PUBLIC_URL + '/data/lavaredo_120.fit');
                  const blob = await response.blob();
                  const fitFile = new File([blob], 'lavaredo_120.fit', { type: 'application/octet-stream' });
                  setFile(fitFile);
                  setReusedFileId(null);
                  setReusedFilename(null);
                  setSelectedPrediction(null);
                  setSelectedRace({ id: 5, name: 'Lavaredo 120K', distance_km: 120.4, elevation_gain_m: 5800, race_type: 'ultra', has_gpx: true });
                  setError('');
                } catch (err) {
                  setError(t('failedToLoadExample', 'Failed to load example file'));
                }
              }}
              sx={{
                color: colors.greenAccent[400],
                borderColor: colors.greenAccent[700],
                textTransform: 'none',
                '&:hover': { borderColor: colors.greenAccent[400], backgroundColor: `${colors.greenAccent[900]}80` },
              }}
            >
              {t('useExampleRunningFile', 'Try Sample Run')}
            </Button>
          )}

          {!isAuthenticated && (
            <Typography variant="caption" color={colors.grey[500]}>
              {t('loginRequired', 'Login to access saved files')}
            </Typography>
          )}
        </Box>

        {/* ── Parameters + Race (only shown when file/prediction is selected) ── */}
        {hasSelection && (
          <>
            <Divider sx={{ borderColor: colors.grey[700], mb: 2.5 }} />

            {/* Parameters + Race — only for actual FIT files, not predictions */}
            {hasFile && (<>
            {/* Description field */}
            <Box mb={2}>
              <TextField
                label={t('fileDescription', 'Description (optional)')}
                size="small"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 200 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors.grey[700] },
                    '&:hover fieldset': { borderColor: colors.grey[500] },
                    '&.Mui-focused fieldset': { borderColor: colors.blueAccent[400] },
                    color: colors.grey[100],
                  },
                  '& .MuiInputLabel-root': { color: colors.grey[400], '&.Mui-focused': { color: colors.blueAccent[400] } },
                  '& .MuiInputBase-input': { color: colors.grey[100] },
                }}
              />
            </Box>

            {/* Race + GPS options row */}
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'flex-start' : 'center'}
              gap={2}
              flexWrap="wrap"
              mb={2.5}
            >
              {/* Race selector */}
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RouteIcon />}
                  onClick={() => setRacePickerOpen(true)}
                  sx={{
                    color: selectedRace ? colors.greenAccent[400] : colors.grey[400],
                    borderColor: selectedRace ? colors.greenAccent[700] : colors.grey[700],
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: selectedRace ? colors.greenAccent[500] : colors.grey[500],
                      backgroundColor: selectedRace ? `${colors.greenAccent[900]}40` : `${colors.grey[700]}40`,
                    },
                    ...(hasFile && !selectedRace && {
                      animation: 'racePulse 0.9s ease-in-out infinite',
                      '@keyframes racePulse': {
                        '0%, 100%': { borderColor: colors.grey[700], color: colors.grey[400] },
                        '50%': {
                          borderColor: colors.blueAccent[400],
                          color: colors.blueAccent[300],
                          boxShadow: `0 0 6px ${colors.blueAccent[700]}`,
                        },
                      },
                    }),
                  }}
                >
                  {selectedRace ? selectedRace.name : t('selectRace', 'Link Race…')}
                </Button>

                {selectedRace && (
                  <>
                    {selectedRace.distance_km && (
                      <Chip
                        label={`${selectedRace.distance_km} km${selectedRace.elevation_gain_m ? ` · +${Math.round(selectedRace.elevation_gain_m)} m` : ''}`}
                        size="small"
                        sx={{ backgroundColor: colors.primary[600], color: colors.grey[300], fontSize: '0.7rem' }}
                      />
                    )}
                    {selectedRace.has_gpx && (
                      <Chip
                        label="GPX"
                        size="small"
                        sx={{ backgroundColor: colors.greenAccent[900], color: colors.greenAccent[300], fontSize: '0.7rem' }}
                      />
                    )}
                    <Tooltip title="Race details">
                      <IconButton
                        size="small"
                        onClick={() => setRaceDetailOpen(true)}
                        sx={{ color: colors.blueAccent[400], '&:hover': { color: colors.blueAccent[300] }, p: 0.25 }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove race link">
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedRace(null); setSnapToGpx(false); }}
                        sx={{ color: colors.grey[600], '&:hover': { color: colors.grey[400] }, p: 0.25 }}
                      >
                        <CloseIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <RaceDetailDialog race={selectedRace} open={raceDetailOpen} onClose={() => setRaceDetailOpen(false)} />

                {/* Asked on the way to Analyze, so it cannot be scrolled past.
                    Both answers proceed to the analysis — the only difference is
                    whether the race comes with it. */}
                <Dialog open={raceConfirmOpen} onClose={() => setRaceConfirmOpen(false)} maxWidth="xs" fullWidth>
                  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RouteIcon fontSize="small" />
                    {t('raceConfirmTitle', 'Is this a race?')}
                  </DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('raceConfirmBody',
                         'Linking the race adds its checkpoints, cutoffs and a race plan, and lets you compare against a prediction. Without it the analysis has none of those.')}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1} mt={2}>
                      {raceSuggestions.map((r) => (
                        <Button
                          key={r.id}
                          variant="outlined"
                          onClick={() => { setSelectedRace(r); setRaceConfirmOpen(false); }}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                          {r.name}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {r.distance_km} km
                            {r.elevation_gain_m ? ` · +${Math.round(r.elevation_gain_m)} m` : ''}
                          </Typography>
                        </Button>
                      ))}
                      <Button
                        size="small"
                        onClick={() => { setRaceConfirmOpen(false); setRacePickerOpen(true); }}
                        sx={{ textTransform: 'none' }}
                      >
                        {t('raceConfirmOther', 'Pick a different race…')}
                      </Button>
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setRaceConfirmOpen(false)} sx={{ textTransform: 'none' }}>
                      {t('raceConfirmSkip', 'Not a race — just analyse it')}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>

              {/* Snap to GPX — close to race button */}
              {selectedRace && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={snapToGpx}
                      onChange={(e) => setSnapToGpx(e.target.checked)}
                      size="small"
                      sx={{ color: colors.grey[600], '&.Mui-checked': { color: colors.blueAccent[400] }, p: 0.5 }}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.25}>
                      <Typography variant="caption" color={colors.grey[400]}>{t('snapToGpx', 'Snap to GPX')}</Typography>
                      <IconButton size="small" onClick={(e) => { e.preventDefault(); setSnapInfoAnchor(e.currentTarget); }} sx={{ color: colors.grey[500], p: 0, mb: 0.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                      <Popover open={Boolean(snapInfoAnchor)} anchorEl={snapInfoAnchor} onClose={() => setSnapInfoAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                        <Box sx={{ p: 2, maxWidth: 300 }}>
                          <Typography variant="subtitle2" gutterBottom>{t('snapToGpxInfoTitle', 'Snap to GPX')}</Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('snapToGpxInfo1', 'Aligns your GPS track to the race course line. Useful when your watch drifted off the route — e.g. on narrow trails or tunnels — and you want distances and elevation to match the official course.')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('snapToGpxInfo2Upload', 'Only available when a race GPX is selected. Also trims any GPS recording your watch continued after you crossed the finish line.')}
                          </Typography>
                        </Box>
                      </Popover>
                    </Box>
                  }
                  sx={{ mr: 0 }}
                />
              )}

              {/* GPS corrections — pushed to the right */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyGpsCorrections}
                    onChange={(e) => setApplyGpsCorrections(e.target.checked)}
                    size="small"
                    sx={{ color: colors.grey[600], '&.Mui-checked': { color: colors.blueAccent[400] }, p: 0.5 }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.25}>
                    <Typography variant="caption" color={colors.grey[400]}>{t('applyGpsCorrections', 'GPS corrections')}</Typography>
                    <IconButton size="small" onClick={(e) => { e.preventDefault(); setGpsInfoAnchor(e.currentTarget); }} sx={{ color: colors.grey[500], p: 0, mb: 0.5 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                    <Popover open={Boolean(gpsInfoAnchor)} anchorEl={gpsInfoAnchor} onClose={() => setGpsInfoAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                      <Box sx={{ p: 2, maxWidth: 300 }}>
                        <Typography variant="subtitle2" gutterBottom>{t('gpsCorrectionsInfoTitle', 'GPS Corrections')}</Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('gpsCorrectionsInfo1', 'Detects and removes GPS spikes — sudden jumps in position that your watch recorded but you never actually ran. These spikes inflate your total distance and can distort pace.')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('gpsCorrectionsInfo2', 'For example, a watch losing signal briefly in a valley and then snapping back can add 200–300m of fake distance. This option filters those out automatically.')}
                        </Typography>
                      </Box>
                    </Popover>
                  </Box>
                }
                sx={{ mr: 0, ml: 'auto' }}
              />
            </Box>
            </>)}

            {/* Analyze / Load Prediction button */}
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                size="large"
                startIcon={busy ? null : <DirectionsRunIcon />}
                onClick={handleSubmit}
                disabled={busy}
                sx={{
                  px: 4,
                  backgroundColor: colors.blueAccent[600],
                  '&:hover': { backgroundColor: colors.blueAccent[500] },
                  '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
                }}
              >
                {busy ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1, color: colors.grey[300] }} />
                    {t('analyzing', 'Analyzing…')}
                  </>
                ) : selectedPrediction ? (
                  t('loadPrediction', 'Load Prediction')
                ) : (
                  t('analyzeRunningWorkout', 'Analyze Running Workout')
                )}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* My Files + Community + My Predictions + Club Files picker */}
      <WorkoutPickerDrawer
        open={filePickerOpen}
        onClose={() => { setFilePickerOpen(false); onFilesDrawerClose?.(); }}
        onSelect={handleFilePickerSelect}
        title={t('myRunningFiles', 'My Running Files')}
        tabs={['myFiles', 'predictions', 'garmin', 'clubFiles', 'clubPredictions', 'community']}
        // Not a picker in the usual sense: people come here to browse, share and
        // mark runs, all of which save as they go. "Cancel" both implied that
        // leaving undid them and failed to read as the way out.
        dismissLabel={t('exit', 'Exit')}
        allowDeleteMyFiles
        allowShareMyFiles
        allowMarkSecondaryRef={isAuthenticated}
        onMultiFilePredict={onMultiFilePredict}
        highlightMarkSignal={markRunsSignal}
        extraTabs={isAuthenticated ? [{
          id: 'garmin',
          label: t('garminTab', 'Garmin'),
          rows: [],
          loading: false,
          columns: [],
          render: ({ selectedId, onSelect, onConfirm }) => (
            <GarminCalendar
              sport="run"
              selectedId={selectedId}
              onSelect={onSelect}
              onConfirm={onConfirm}
            />
          ),
          getSource: (row) => ({ type: 'garmin', activityId: row.id }),
        }, {
          id: 'clubFiles',
          label: t('comparisonClubFiles', 'Club Files'),
          rows: allClubFiles,
          loading: allClubFilesLoading,
          columns: [
            { field: 'club_name', headerName: t('club', 'Club'), width: 110 },
            { field: 'contributor_email', headerName: t('member', 'Member'), width: 140 },
            {
              field: 'filename', headerName: t('fileColumn', 'File'), flex: 1, minWidth: 120,
              valueFormatter: (v) => v || '—',
            },
            {
              field: 'location_name', headerName: '📍 Location', flex: 1, minWidth: 100,
              valueFormatter: (v) => v || '—',
            },
            { field: 'race_name', headerName: t('race', 'Race'), width: 100, valueFormatter: (v) => v || '—' },
            {
              field: 'workout_date', headerName: t('date', 'Date'), width: 100,
              valueFormatter: (v) => v ? new Date(v).toLocaleDateString() : '—',
            },
            {
              field: 'total_distance', headerName: t('dist', 'Dist'), width: 75,
              valueFormatter: (v) => v == null ? '—' : `${(v / 1000).toFixed(1)} km`,
            },
            {
              field: 'total_duration', headerName: t('time', 'Time'), width: 75,
              valueFormatter: (v) => {
                if (v == null) return '—';
                const h = Math.floor(v / 3600), m = Math.floor((v % 3600) / 60);
                return h > 0 ? `${h}:${String(m).padStart(2,'0')}h` : `${m}:${String(Math.floor(v%60)).padStart(2,'0')}`;
              },
            },
          ],
          getSource: (row) => ({
            type: 'clubFile',
            clubId: row.club_id,
            clubFileId: row.id,
            clubName: row.club_name,
            contributorEmail: row.contributor_email,
            filename: row.filename,
          }),
        }] : []}
      />

      {/* Race picker */}
      <WorkoutPickerDrawer
        open={racePickerOpen}
        onClose={() => setRacePickerOpen(false)}
        onSelect={handleRacePickerSelect}
        title={t('selectRace', 'Select Race')}
        tabs={['races']}
        actionLabel={t('linkRace', 'Link Race')}
      />

      {/* Long wait message snackbar */}
      <Snackbar
        open={showLongWaitMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t('analysisLongWait')}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: '14px'
          }
        }}
      />
      <ClubPickerDrawer
        open={clubPickerOpen}
        onClose={() => setClubPickerOpen(false)}
      />
    </Box>
  );
};

export default RunningFileUpload;
